import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ChevronDown, Megaphone, ArrowRight, Rocket } from "lucide-react";
import { api } from "@/lib/api";
import { Loader, ProgressBar } from "@/components/common";
import { useTx } from "@/components/PaymentPanel";

function MissionAnswer({ m, onSaved }) {
  const tx = useTx();
  const [v, setV] = useState(m.answer || "");
  const [state, setState] = useState("");
  const saved = useRef(m.answer || "");
  const timer = useRef(null);
  const save = (text) => {
    clearTimeout(timer.current);
    if (text === saved.current) return;
    setState("saving");
    api.put(`/launch-plan/missions/${m.id}/answer`, { text }).then(() => { saved.current = text; setState("saved"); onSaved(); }).catch(() => setState("error"));
  };
  const change = (text) => { setV(text); setState(""); clearTimeout(timer.current); timer.current = setTimeout(() => save(text), 1000); };
  useEffect(() => () => clearTimeout(timer.current), []);
  const label = { saving: tx("Saving…", "Enregistrement…"), saved: tx("Saved to your business", "Enregistré dans votre business"), error: tx("Not saved, try again", "Non enregistré, réessayez") }[state];
  return (
    <div className="mt-2.5 pl-9">
      <textarea value={v} onChange={(e) => change(e.target.value)} onBlur={() => save(v)} maxLength={2000} rows={2} placeholder={tx("Write your answer here…", "Écrivez votre réponse ici…")}
        className="w-full rounded-xl border border-[#EADEC9] bg-white p-3 text-base focus:outline-none focus:ring-2 focus:ring-[#1DB99A]" data-testid={`mission-answer-${m.id}`} />
      {label && <div className={`text-xs font-semibold mt-1 ${state === "error" ? "text-red-600" : "text-[#179B80]"}`} data-testid={`mission-answer-status-${m.id}`}>{label}</div>}
    </div>
  );
}

export function MissionList({ missions, onToggle, onSaved }) {
  return (
    <ul className="space-y-2">
      {missions.map((m) => (
        <li key={m.id} className={`rounded-2xl border p-3.5 ${m.done ? "border-[#1DB99A] bg-[#E8F8F4]" : "border-[#EADEC9] bg-white"}`} data-testid={`mission-${m.id}`}>
          <label className="flex gap-3 items-start cursor-pointer">
            <input type="checkbox" checked={m.done} onChange={(e) => onToggle(m.id, e.target.checked)} className="mt-0.5 h-6 w-6 shrink-0 accent-[#1DB99A]" data-testid={`mission-check-${m.id}`} />
            <span><span className={`block font-semibold ${m.done ? "text-[#5C6B73]" : ""}`}>{m.title}</span><span className="block text-sm text-[#5C6B73] mt-0.5">{m.how}</span></span>
          </label>
          <MissionAnswer m={m} onSaved={onSaved} />
        </li>
      ))}
    </ul>
  );
}

export const toggleMission = (id, done, reload) => api.post(`/launch-plan/missions/${id}`, { done }).then(reload);

function StageRow({ s, open, onOpen, reload }) {
  const tx = useTx();
  const nav = useNavigate();
  const complete = s.done === s.total;
  return (
    <div className="sb-card overflow-hidden" data-testid={`launch-stage-${s.n}`}>
      <button onClick={() => (s.key === "promote" ? nav("/launch/promote") : onOpen())} className="w-full flex items-center gap-3 p-4 text-left" data-testid={`launch-stage-btn-${s.n}`}>
        <span className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${complete ? "bg-[#1DB99A] text-white" : "bg-[#F5F0E6]"}`}>{complete ? <Check className="h-5 w-5" strokeWidth={3} /> : s.n}</span>
        <span className="flex-1 min-w-0"><span className="block font-bold">{s.title}</span><span className="block text-xs text-[#5C6B73]">{s.done}/{s.total} {tx("missions", "missions")}</span></span>
        {s.key === "promote" ? <Megaphone className="h-5 w-5 text-[#1DB99A]" /> : <ChevronDown className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && s.key !== "promote" && <div className="px-4 pb-4"><MissionList missions={s.missions} onToggle={(id, v) => toggleMission(id, v, reload)} onSaved={reload} /></div>}
    </div>
  );
}

export default function LaunchPlan() {
  const tx = useTx();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState(false);
  const load = () => api.get("/launch-plan").then((r) => { setD(r.data); setOpen((o) => o ?? r.data.current_stage.n); }).catch(() => setErr(true));
  useEffect(() => { load(); }, []);
  if (err) return <div className="sb-card p-6" data-testid="launch-no-business"><p className="font-bold">{tx("Choose a business idea first.", "Choisissez d’abord une idée.")}</p><Link to="/ideas" className="sb-btn-primary mt-3">{tx("Explore ideas", "Voir les idées")}</Link></div>;
  if (!d) return <Loader />;
  return (
    <div className="max-w-2xl space-y-4" data-testid="launch-plan-page">
      <section className="rounded-3xl bg-[#1E2B3C] text-white p-6">
        <div className="sb-eyebrow text-[#1DB99A] flex items-center gap-2"><Rocket className="h-4 w-4" />{tx("My Business Launch Plan", "Mon plan de lancement")}</div>
        <h1 className="text-2xl sm:text-3xl font-extrabold mt-2" data-testid="launch-business-name">{tx("Your Business", "Votre business")}: {d.business_name}</h1>
        <p className="text-white/75 mt-2">{tx("Let’s turn your idea into a real business! Follow simple steps to prepare your offer, find customers, promote your service and get started.", "Transformons votre idée en vrai business ! Suivez des étapes simples pour préparer votre offre, trouver des clients et vous promouvoir.")}</p>
        <div className="mt-4"><div className="flex justify-between text-xs font-semibold mb-1"><span>{tx("Stage", "Étape")} {d.current_stage.n}/9 · {d.current_stage.title}</span><span data-testid="launch-pct">{d.pct}%</span></div><ProgressBar value={d.pct} tall /></div>
        {d.next_mission && <p className="text-sm mt-3" data-testid="launch-next-mission"><b className="text-[#1DB99A]">{tx("Next", "Prochaine")}:</b> {d.next_mission.title}</p>}
      </section>
      {d.stages.map((s) => <StageRow key={s.key} s={s} open={open === s.n} onOpen={() => setOpen(open === s.n ? 0 : s.n)} reload={load} />)}
    </div>
  );
}

export function LaunchNextStep({ d }) {
  const tx = useTx();
  const m = d.next_mission;
  return (
    <section className="rounded-2xl bg-[#1E2B3C] text-white p-5 sm:p-7 relative overflow-hidden" data-testid="home-next-step-card">
      <div className="absolute right-0 top-0 h-full w-1.5 bg-[#1DB99A]" />
      <div className="sb-eyebrow text-[#1DB99A] flex items-center gap-2"><Rocket className="h-4 w-4" />{tx("NEXT STEP", "PROCHAINE ÉTAPE")} · {tx("MY LAUNCH PLAN", "MON PLAN DE LANCEMENT")}</div>
      <div className="text-xs text-white/60 mt-2" data-testid="home-next-step-stage">{tx("Stage", "Étape")} {d.current_stage.n}/9 · {d.current_stage.title}</div>
      <h2 className="text-xl sm:text-2xl font-extrabold mt-1" data-testid="home-next-step-title">{m ? m.title : tx("Your launch plan is complete!", "Votre plan de lancement est terminé !")}</h2>
      {m && <p className="text-sm text-white/70 mt-2 leading-relaxed max-w-xl">{m.how}</p>}
      <div className="mt-4 max-w-md"><div className="flex justify-between text-xs font-semibold mb-1"><span>{d.business_name}</span><span data-testid="home-launch-pct">{d.pct}%</span></div><ProgressBar value={d.pct} /></div>
      <Link to={d.current_stage.key === "promote" ? "/launch/promote" : "/launch"} className="sb-btn-primary mt-5 w-full sm:w-auto" data-testid="home-continue-launch-btn">{tx("Continue My Launch Plan", "Continuer mon plan")} <ArrowRight className="h-4 w-4" /></Link>
    </section>
  );
}
